/**
 * Shared hydration helper for the exporters.
 *
 * A lead's stored designState is the SERIALIZED form; @dockpro/core's take-off
 * and exporter functions need a hydrated state (`grid: Set`, world-space
 * geometry). `hydrateDesign` bridges that gap once, so the BOM, PDF, and DXF
 * exporters share a single entry point.
 */
import { loadPresetIntoState, computeDockModel } from "@dockpro/core";
import { resolveBayCatalog } from "@dockpro/core/catalogs";

// DockModelState isn't a named core export — derive it from computeDockModel's
// (JSDoc-emitted) parameter type. Was a hand-written declaration pre-R24.5.
type DockModelState = Parameters<typeof computeDockModel>[0];

/**
 * Hydrate a serialized designState into the shape the core exporters expect.
 * loadPresetIntoState runs the schema-migration ladder + slip carve, then we
 * remap its `cells` Set to `grid`. Returns null for empty/minimal designs (no
 * usable geometry) or unknown schema versions — never throws.
 */
/**
 * round9/embed-config: the tenant's bay catalog (stock + custom slip sizes).
 * A saved design stores a bay by `sizeKey` only and re-resolves its footprint
 * from the catalog on every load, so WITHOUT the tenant's custom bays a lead
 * that uses one would export/estimate with the slip silently dropped. Fails
 * open to the stock catalog on a malformed list (the backend validates at
 * write time, so that is corrupt data, not a user error).
 */
function bayCatalogFor(customBoatBays: unknown): object | undefined {
  if (!Array.isArray(customBoatBays) || customBoatBays.length === 0) return undefined;
  try {
    return resolveBayCatalog(customBoatBays);
  } catch {
    return undefined;
  }
}

export function hydrateDesign(
  designState: unknown,
  opts: { customBoatBays?: unknown } = {},
): DockModelState | null {
  let r;
  try {
    r = loadPresetIntoState(designState, { bayCatalog: bayCatalogFor(opts.customBoatBays) });
  } catch {
    // migrate() throws UnknownSchemaVersionError on future-version data.
    return null;
  }
  if (!r) return null;
  const site = r as { distanceFromShoreFt?: number | null; waterDepthFt?: number | null };
  return {
    grid: r.cells,
    outline: r.outline,
    features: r.features,
    material: r.material,
    dockHeight: r.dockHeight,
    dockType: r.dockType,
    pilingStyle: r.pilingStyle,
    railColor: r.railColor,
    gangwayWidthFt: r.gangwayWidthFt ?? 4,
    gangwayLengthFt: r.gangwayLengthFt ?? 6,
    shorelineEnabled: r.shorelineEnabled ?? false,
    gangwayT: r.gangwayT ?? 0.5,
    // Round 22 surveyed site data — thread it through so the PDF elevation can
    // draw the mudline at the homeowner's water depth. core 1.1.2 carries these
    // at runtime (loadPresetIntoState returns them; computeDockModel reads them),
    // but its DockModelState/HydratedDockState TS types don't declare them yet —
    // so read/emit via a local cast until a core type bump (1.1.3).
    distanceFromShoreFt: site.distanceFromShoreFt ?? null,
    waterDepthFt: site.waterDepthFt ?? null,
  } as DockModelState;
}
